import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Select, NumberInput } from '@mantine/core';
import { useDebounceFn } from 'ahooks';
import { translate } from '@shakkerai/util-i18n';
import { Icon } from '@shakkerai/components';
import { NumberInputGroup } from '@shakkerai/pro-components';
import { setTempState, useTempState } from '@starflow/app/store';
import { comfySamplerOptions, comfySchedulerOptions } from '@shakkerai/pro-constants';
import { samplerMethodOptions, presetNegativePromptOptions } from '@shakkerai/pro-constants';
import { MODEL_SDXL, MODEL_SD15, SDXL_MODELS_FAMILY } from '@shakkerai/pro-models';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { AdvancedParams } from './advanced';

const widthFreeSelectComboxProps = {
  position: 'bottom-end',
  width: '!auto',
};

const selectClassNames = {
  dropdown: 'dark-select-dropdown',
  input: 'text-right !pr-6',
};

const seedOptions = [
  { value: '0', label: translate('parameter_seeds_random') },
  { value: '1', label: translate('parameter_seeds_fixed') },
];

const MAX_SEED_NUMBER = 9999999999;

const handleSeedNumberKeyDown = (event) => {
  if (event.key === 'Enter' || event.key === 'e' || event.nativeEvent.isComposing) {
    event.preventDefault();
    event.nativeEvent.preventDefault();
  }
};

export const NegPrompt = React.memo((props) => {
  const { disabled, generator } = props;
  const negPromptOpened = useTempState('negPromptOpened');
  const { negativePrompt, presetNegativePrompts } = generator.data;
  const [inputNegativePrompt, setInputNegativePrompt] = useState(negativePrompt);
  const updateUnitDataDebounced = useDebounceFn(updateUnitData, { wait: 200 });

  const baseType = generator.data.baseType || MODEL_SDXL;

  const handleToggleOpened = useCallback(() => {
    setTempState({ negPromptOpened: !negPromptOpened });
  }, [negPromptOpened]);

  const handleUpdatePrompt = useCallback(
    (negativePrompt) => {
      setInputNegativePrompt(negativePrompt);
      updateUnitDataDebounced.run(generator.id, { negativePrompt });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generator.id],
  );

  const handleInputNegativePrompt = useCallback(
    (event) => {
      const inputPrompt = event.target.value;
      handleUpdatePrompt(inputPrompt);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleUpdatePrompt],
  );

  const handleTogglePreset = useCallback(
    (type) => {
      const nextPresetNegativePrompts = presetNegativePrompts.includes(type)
        ? presetNegativePrompts.filter((item) => item !== type)
        : [...presetNegativePrompts, type];

      updateUnitData(generator.id, { presetNegativePrompts: nextPresetNegativePrompts });
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presetNegativePrompts, generator.id],
  );

  return (
    <div className="mt-3">
      <div onClick={handleToggleOpened} className="flex items-center justify-between h-6 cursor-pointer">
        <span className="text-xs text-[#7f7f7f]">{translate('prompt_neg_title')}</span>
        <Icon className={`shk-kuangjia-zhankai ${negPromptOpened ? 'rotate-180' : ''}`} fontSize={14} color="#7f7f7f" />
      </div>
      {negPromptOpened && (
        <div className="fllex flex-col items-stretch mt-2 bg-[#f9fbfb] border border-[#efefef] rounded-[2px]">
          <textarea
            disabled={disabled}
            value={inputNegativePrompt}
            onChange={handleInputNegativePrompt}
            placeholder={translate('prompt_negative_placeholder')}
            className="w-full h-16 m-0 p-3 resize-none bg-transparent border-b border-[#efefef] text-xs text-[#474747] placeholder:text-[#b2b2b2]"
          />
          <div className="flex flex-wrap px-3 py-2 pt-2">
            {presetNegativePromptOptions.map((option) => (
              <button
                key={option.value}
                className="flex items-center mr-3 mb-1.5"
                disabled={SDXL_MODELS_FAMILY[baseType] || disabled}
                onClick={() => handleTogglePreset(option.value)}
              >
                {baseType == MODEL_SD15 && presetNegativePrompts.includes(option.value) ? (
                  <div className="flex items-center justify-center w-3 h-3 bg-primary rounded-[1px]">
                    <Icon className="icon-border_check" fontSize={10} color="#fff" />
                  </div>
                ) : (
                  <div className="w-3 h-3 bg-white border border-black/10 rounded-[1px]" />
                )}
                <span className="ml-1 leading-[12px] translate-y-px text-xs text-[#7f7f7f]">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export const BaseParams = React.memo((props) => {
  const { disabled, generator } = props;
  const [inputKey, setInputKey] = useState(Date.now());
  const [seedNumber, setSeedNumber] = useState(generator.data.seedNumber);
  const updateUnitDataDebounced = useDebounceFn(updateUnitData, { wait: 200 });
  const baseType = generator.data?.baseType;

  const openedSubWindow = useTempState('exclusiveWindow');
  const advancedParamsOpened = openedSubWindow === 'advanced-generate-options';
  const isSimple = useTempState('globalMode') === 'simple';

  const fixedSamplerMethodOptions = useMemo(() => {
    return samplerMethodOptions.map((item) => {
      const disabled = !item.baseTypes.includes(baseType);

      return {
        ...item,
        disabled,
      };
    });
  }, [baseType]);

  const updateInputKey = useCallback(() => {
    setInputKey(Date.now());
  }, []);

  const handleChange = useCallback(
    (value, name) => {
      updateUnitData(generator.id, { [name]: value });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [generator.id],
  );

  const handleInputSeedNumber = useCallback(
    (value) => {
      setSeedNumber(value);
      updateUnitDataDebounced.run(generator.id, {
        seedNumber: value !== '' ? Math.min(MAX_SEED_NUMBER, Math.max(1, value)) : '',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generator.id],
  );

  const isOutpaint = generator.type === 'outpaint';
  const isPrimary = generator.type === 'generator';
  const isChangeFace = generator.type === 'changeFace';
  const isStarFlow = generator?.data?.presetBaseModelId === 'base_checkpoint';

  useEffect(() => {
    setSeedNumber(generator.data.seedNumber);
  }, [generator.data.seedNumber]);

  const fixedSamplerMethodOpts = useCallback(() => {
    const _disable = [];
    const _nonDisable = [];
    fixedSamplerMethodOptions.forEach((fi) => {
      if (fi.disabled) {
        _disable.push(fi);
      } else {
        _nonDisable.push(fi);
      }
    });
    return _nonDisable.concat(_disable);
  }, [fixedSamplerMethodOptions]);
  return (
    <div className="flex flex-col items-stretch w-full">
      <div className="flex items-center w-full justify-between mb-3">
        <h5 className="flex-1 text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
          {translate('parameter_title')}
        </h5>
        <button
          onClick={() => setTempState({ exclusiveWindow: advancedParamsOpened ? null : 'advanced-generate-options' })}
          id={`advanced-options-entry-${generator?.id}`}
          data-trigger-window="advanced-generate-options"
          className="flex items-center justify-center w-[18px] h-[18px] rounded-sm hover:bg-[#f2f4f6] text-[#7A7A7A] disabled:opacity-30"
        >
          <Icon className="shk-kuangjia-gengduoshezhi" fontSize={14} color="#666" />
        </button>
      </div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-[#7f7f7f]">{translate('parameter_sampler')}</span>
        <div className="flex items-center h-6">
          {isOutpaint || isChangeFace ? (
            <Select
              size="xxxs"
              data={comfySamplerOptions}
              disabled={disabled}
              value={generator.data.sampler || 'dpmpp_2m'}
              classNames={selectClassNames}
              className="cn-select flex-1 bordered"
              comboboxProps={{ width: 250 }}
              onChange={(value) => handleChange(value, 'sampler')}
            />
          ) : (
            <Select
              size="xxxs"
              data={fixedSamplerMethodOpts()}
              disabled={disabled}
              value={generator.data.samplerMethod}
              classNames={selectClassNames}
              className="cn-select flex-1 bordered"
              comboboxProps={{ width: 250 }}
              onChange={(value) => handleChange(value, 'samplerMethod')}
            />
          )}
        </div>
      </div>
      {(isOutpaint || isChangeFace) && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-[#7f7f7f]">{translate('parameter_scheduler')}</span>
          <div className="flex items-center h-6">
            <Select
              size="xxxs"
              data={comfySchedulerOptions}
              disabled={disabled}
              value={generator.data.scheduler || 'karras'}
              classNames={selectClassNames}
              className="cn-select flex-1 bordered"
              comboboxProps={{ width: 250 }}
              onChange={(value) => handleChange(value, 'scheduler')}
            />
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-[#7f7f7f]">{translate('parameter_steps')}</span>
        <div className="flex w-40 items-center h-6">
          <NumberInputGroup
            min={1}
            max={60}
            step={1}
            disabled={disabled}
            allowDecimal={false}
            name="samplingSteps"
            inputWidth={45}
            value={generator.data.samplingSteps}
            onChange={handleChange}
          />
        </div>
      </div>
      {!isStarFlow && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-[#7f7f7f]">{translate('parameter_seeds')}</span>
          <div className="flex-1" />
          {generator.data.seedType === '1' ? (
            <NumberInput
              type="number"
              size="xxs"
              min={1}
              max={MAX_SEED_NUMBER}
              hideControls
              key={inputKey}
              onKeyDown={handleSeedNumberKeyDown}
              onCompositionUpdate={updateInputKey}
              onCompositionEnd={updateInputKey}
              allowDecimal={false}
              allowNegative={false}
              value={seedNumber}
              isAllowed={(value) => value !== 'e'}
              onChange={handleInputSeedNumber}
              disabled={generator.data.seedType === '0' || disabled}
              placeholder={generator.data.seedType === '0' ? '' : translate('parameter_seeds_placeholder')}
              className="mini-number-input w-28 ml-2 text-xs text-center text-caption-500"
            />
          ) : null}
          <div className="flex-shrink-0 ml-1">
            <Select
              w={80}
              size="xxxs"
              data={seedOptions}
              disabled={disabled}
              classNames={selectClassNames}
              value={generator.data.seedType}
              className="cn-select flex-1 bordered"
              comboboxProps={widthFreeSelectComboxProps}
              onChange={(value) => handleChange(value, 'seedType')}
            />
          </div>
        </div>
      )}
      {!isSimple && !isChangeFace && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#7f7f7f]">{translate('parameter_cfg')}</span>
          {/* CFG Scale */}
          <div className="flex w-40 items-center h-6">
            <NumberInputGroup
              min={1}
              max={30}
              step={0.5}
              name="cfgScale"
              inputWidth={32}
              disabled={disabled}
              value={generator.data.cfgScale}
              onChange={handleChange}
            />
          </div>
        </div>
      )}
      {!isPrimary && <NegPrompt disabled={disabled} generator={generator} />}
      {advancedParamsOpened && <AdvancedParams disabled={disabled} isSimple={isSimple} generator={generator} />}
    </div>
  );
});
